import { inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { AwsLoginService } from '@domains/login/services/aws-login.service';
import { SignUpService } from '@domains/login/services/sign-up.service';
import { handleLoginRequest } from '@domains/login/utils/handleLoginOperator';
import { UntilDestroy } from '@ngneat/until-destroy';
import { OAuthCredential, RoutingPath, SignalRService, TempTokenData, TFAData } from 'mbs-ui-shared';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { AuthCredentials, AuthGoogleCredentials, AuthIdCredentials, AuthMicrosoftCredentials } from 'mbs-ui-shared@models/auth-models';
import { AwsCreatePayload, AwsSetPayload, DataForLoginUser, SignUpData, SignUpExternalData } from 'mbs-ui-shared@models/login';
import { TwoFactorAuthenticationTypeMap } from 'mbs-ui-shared@models/tfa';
import { OneTimeSessionService } from 'mbs-ui-shared@services/auth/one-time-session.service';
import { first, noop, Observable } from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class MbsLoginFacade extends LoginFacade {
  private signUpService = inject(SignUpService);
  private awsLoginService = inject(AwsLoginService);
  private signalRService = inject(SignalRService);
  private oneTimeSession = inject(OneTimeSessionService);

  public dataForLoginUserName = 'dataForLoginUser';
  public dataForLoginUser: WritableSignal<DataForLoginUser> = signal(null);

  public getInfo(alias: string) {
    return this.loginService.getInfo(alias);
  }

  public getUserInfo(alias: string, data: DataForLoginUser) {
    return this.loginService.getUserInfo(alias, data.providerId);
  }

  public getSignUpInfo() {
    return this.signUpService.getSignUpInfo();
  }

  public signUp(credentials: SignUpData, alias?: string) {
    const request = this.signUpService.signUp(credentials, alias).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public signUpByGoogle(credentials: SignUpExternalData, alias?: string) {
    const request = this.signUpService.signUpByGoogle(credentials, alias).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public signUpByMicrosoft(credentials: SignUpExternalData, alias?: string) {
    const request = this.signUpService.signUpByMicrosoft(credentials, alias).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public createAWSUser(payload: AwsCreatePayload) {
    return this.awsLoginService.createAWSUser(payload);
  }

  public setAWSUser(payload: AwsSetPayload) {
    return this.awsLoginService.setAWSUser(payload);
  }

  public getAwsUser() {
    return this.awsLoginService.getAwsUser();
  }

  public sendRestorePasswordEmail(email: string) {
    return this.loginService.sendRestorePasswordEmail(email);
  }

  public loginInternalByCredentials(credentials: AuthCredentials) {
    const request = this.auth.loginByCredentialsInternal(credentials).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public loginByCredentials(credentials: AuthCredentials) {
    const request = this.auth.loginByCredentials(credentials).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public loginOneTimeByCredentials(credentials: AuthCredentials) {
    const request = this.auth
      .loginByCredentials({ ...credentials, oneTimeSession: true, appLoginAsAdmin: true })
      .pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleOneTimeSessionLogin(request);
  }

  public getTempTokenForAdmin(userId: string, computerHid: string): Observable<TempTokenData> {
    return this.auth.getTempTokenForAdmin(userId, computerHid).pipe();
  }

  public loginUserByCredentials(credentials: AuthCredentials, data: DataForLoginUser): Observable<OAuthCredential> {
    return this.auth.loginUserByCredentials(credentials, data).pipe(handleLoginRequest(noop));
  }

  public loginByQr(rememberMe: boolean) {
    const request = this.auth.loginByQr(rememberMe).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public loginByGoogle(credentials: AuthGoogleCredentials) {
    const request = this.auth.loginByGoogle(credentials).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public loginOneTimeByGoogle(credentials: AuthGoogleCredentials) {
    const request = this.auth
      .loginByGoogle({ ...credentials, oneTimeSession: true, appLoginAsAdmin: true })
      .pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleOneTimeSessionLogin(request);
  }

  public loginUserByGoogle(credentials: AuthGoogleCredentials): Observable<OAuthCredential> {
    return this.auth.loginUserByGoogle(credentials).pipe(handleLoginRequest(noop));
  }

  public loginByMicrosoft(credentials: AuthMicrosoftCredentials) {
    const request = this.auth.loginByMicrosoft(credentials).pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleLogin(request);
  }

  public loginOneTimeByMicrosoft(credentials: AuthMicrosoftCredentials) {
    const request = this.auth
      .loginByMicrosoft({ ...credentials, oneTimeSession: true, appLoginAsAdmin: true })
      .pipe(handleLoginRequest((error) => this.handleTFA(error)));

    return this.handleOneTimeSessionLogin(request);
  }

  public loginUserByMicrosoft(credentials: AuthMicrosoftCredentials): Observable<OAuthCredential> {
    return this.auth.loginUserByMicrosoft(credentials).pipe(handleLoginRequest(noop));
  }

  public loginByAdminToken(token: string) {
    const request = this.auth.loginByAdminToken(token).pipe(handleLoginRequest());

    return this.handleLogin(request);
  }

  public loginByAuthId(credentials: AuthIdCredentials) {
    const request = this.auth.loginByAuthId(credentials).pipe(handleLoginRequest());

    return this.oneTimeSession.isAwaitedTwoFactorAuth(credentials.authId)
      ? this.handleOneTimeSessionLogin(request)
      : this.handleLogin(request);
  }

  public getOneTimeSessionCredentials(): OAuthCredential {
    return this.oneTimeSession.credentials;
  }

  public beginOneTimeSession() {
    this.oneTimeSession.begin();
  }

  public endOneTimeSession(onlyLogin?: boolean) {
    this.oneTimeSession.end(onlyLogin);
  }

  public handleAuthenticatedUser() {
    this.auth.authToken && this.redirectToApp();
  }

  override logout(path: string = RoutingPath.Login, ref?: string) {
    if (this.oneTimeSession.isActive) {
      this.oneTimeSession.end();
      location.href = RoutingPath.Login;

      return;
    }

    this.logoutEvent.next(true);

    this.redirectToLogin(path, ref);
  }

  override redirectToLogin(path: string = RoutingPath.Login, ref?: string) {
    super.redirectToLogin(path, ref || this.getRequestedRef());
  }

  public logoutWithState(path: string = RoutingPath.Login, ref?: string) {
    this.redirectToLoginWithState(path, ref);
  }

  override getLogoutRequest() {
    return this.auth.logout();
  }

  public redirectToStayLoggedIn() {
    this.router.navigate([RoutingPath.ApStayLoggedIn], { queryParamsHandling: 'preserve' });
  }

  override redirectToApp(ignoredRef = RoutingPath.Unauthorized) {
    const { ref, isAbsolutePath } = this.route.snapshot.queryParams;

    const route = ref?.startsWith(ignoredRef) || !ref ? RoutingPath.ApDashboard : ref;

    isAbsolutePath ? (location.href = ref) : this.router.navigateByUrl(route);
  }

  public redirectToSupportPortalLanding() {
    const { ref } = this.route.snapshot.queryParams;
    this.auth.currentUser$.pipe(first()).subscribe((res) => {
      this.router
        .navigate([ref], {
          queryParams: {
            login_as: res?.Email
          }
        })
        .then();
    });
  }

  public redirectToEnablingTFA() {
    this.router.navigateByUrl(RoutingPath.EnableTFA);
  }

  public redirectToLoginAdminAndSetUser() {
    this.router.navigateByUrl(RoutingPath.AuthorizationComputer, { replaceUrl: true });
  }

  public getQrCode() {
    return this.auth.getQrCode();
  }

  override clear() {
    super.clear();
    this.signalRService.stopReconnection();
    this.signalRService.stopConnections();
  }

  private getRequestedRef(): string {
    const routerUrl = this.router.url;

    if (routerUrl && routerUrl !== '/') return routerUrl;

    const requestedUrl = `${location.pathname}${location.search}`;

    return requestedUrl.startsWith(RoutingPath.Unauthorized) ? routerUrl : requestedUrl;
  }

  private handleTFA(data: TFAData) {
    const { ref } = this.route.snapshot.queryParams;
    const dataForLoginUser = untracked(() => this.dataForLoginUser());

    if (this.oneTimeSession.isActive) this.oneTimeSession.awaitTwoFactorAuth(data.authId);

    this.router.navigate(['AS/Auth2FA'], {
      queryParams: {
        authId: data.authId,
        type: TwoFactorAuthenticationTypeMap[data.type],
        exp: data.timeExpired,
        ip: null,
        doNotDisturbEnabled: null,
        ref: dataForLoginUser ? RoutingPath.AuthorizationComputer : ref
      }
    });
  }
}
